import React, { useEffect, useState } from 'react';
import { Text } from '@gjensidige/builders-components';
import {
  CalculationWidget,
  CalculationWidgetProps
} from '@gjensidige/se-common-calculation';
import { validateSSN } from '@gjensidige/se-helpers';

import { CustomErrorModal } from '../components/CustomErrorModal';
import { WidgetProps } from '../interfaces/Types';
import { AppHelper } from '../utils/AppHelper';
import { ageResolver } from '../utils/CalculationHelper';

interface InputWidgetProps {
  enonicData: WidgetProps;
}

export const InputWidgetContainer: React.FC<InputWidgetProps> = ({
  enonicData
}: InputWidgetProps) => {
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [personNumber, setPersonNumber] = useState('');
  const [enableButton, setEnableButton] = useState(false);
  const [validRegNumber, isValidRegNo] = useState(false);
  const [validPersonNumber, isValidPersonNumber] = useState(false);
  const [regNumberErrorText, setRegNumberErrorText] = useState('');
  const [personNumberErrorText, setPersonNumberErrorText] = useState('');
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);

  const MIN_YEAR_PREFIX = 19;
  const MIN_DRIVER_AGE = 15;
  const MIN_AGE_TO_CALL_CUSTOMER = 15;
  const MAX_AGE_TO_CALL_CUSTOMER = 18;
  const NEXT_PAGE_URL_PATH = '/links/webshop-moped';

  useEffect(() => {
    setEnableButton(validRegNumber && validPersonNumber);
  }, [validRegNumber, validPersonNumber]);

  const handleOnChangePersonNumber = (event: any) => {
    event.preventDefault();
    const customerInput = event.currentTarget.value.replace(/\D/g, '');
    setPersonNumber(customerInput.toUpperCase());
    const isPersonNumberValid = cprAndAgeValidator(
      { age: MIN_DRIVER_AGE },
      customerInput
    );
    isValidPersonNumber(isPersonNumberValid);
    if (!customerInput.length) {
      setPersonNumberErrorText('');
    }
  };

  const handleOnBlurPersonNumber = (event: any) => {
    event.preventDefault();
    const customerInput = event.currentTarget.value.replace(/\D/g, '');
    if (!customerInput.length) {
      setPersonNumberErrorText('');
    } else {
      if (validPersonNumber) {
        setPersonNumberErrorText('');
      } else {
        if (!validateSSN(customerInput)) {
          setPersonNumberErrorText(enonicData.cprInvalidMessage);
        } else if (ageResolver(customerInput) < MIN_DRIVER_AGE) {
          setPersonNumberErrorText(enonicData.ageBelow15Text);
        }
      }
    }
  };

  const handleOnChangeRegNo = (event: any) => {
    event.preventDefault();
    const customerInput = event.currentTarget.value.trim().toUpperCase();
    const validRegNumber = new RegExp(/([A-Z]{3})([0-9]{2})([0-9A-Z])/).test(
      customerInput
    );
    const finalValidRegNumber = customerInput.length === 6 && validRegNumber;
    setRegistrationNumber(customerInput.toUpperCase());
    isValidRegNo(finalValidRegNumber);
    if (!customerInput.length) {
      setRegNumberErrorText('');
    }
  };

  const handleOnBlurRegNo = (event: any) => {
    event.preventDefault();
    const customerInput = event.currentTarget.value.trim().toUpperCase();
    if (!customerInput.length) {
      setRegNumberErrorText('');
    } else {
      if (validRegNumber) {
        setRegNumberErrorText('');
      } else {
        setRegNumberErrorText(enonicData.regNumberInvalidMessage);
      }
    }
  };

  const cprAndAgeValidator = (options: any, inputValue: any) => {
    if (
      (inputValue.length !== 10 && inputValue.length !== 12) ||
      (inputValue.length === 12 &&
        Number(inputValue.slice(0, 2)) < MIN_YEAR_PREFIX) ||
      !validateSSN(inputValue)
    ) {
      return false;
    } else {
      if (ageResolver(inputValue) < options.age) {
        return false;
      } else return true;
    }
  };

  const gotoNextStep = (event: any) => {
    event.preventDefault();
    if (
      ageResolver(personNumber) >= MIN_AGE_TO_CALL_CUSTOMER &&
      ageResolver(personNumber) < MAX_AGE_TO_CALL_CUSTOMER
    ) {
      setIsValidationModalOpen(true);
    } else {
      const customerInfo = {
        cprNumber: personNumber,
        registrationNumber: registrationNumber
      };
      sessionStorage.setItem('customerInfo', JSON.stringify(customerInfo));
      if (NEXT_PAGE_URL_PATH) {
        window.location.href = window.location.origin + NEXT_PAGE_URL_PATH;
      }
    }
  };

  const getCprMoreInfoContent = () => {
    return (
      <Text>
        Vi behöver ditt personnummer för att kunna ge dig rätt pris.
        Personnumret ger oss bland annat uppgifter om var du bor och din ålder.
      </Text>
    );
  };

  const motorcycleParams: CalculationWidgetProps = {
    headerText: enonicData.headerText,
    image: AppHelper.getImageCDNPath(enonicData.imageInfo.image),
    smallScreenImage: AppHelper.getImageCDNPath(
      enonicData.imageInfo.smallScreenImage
    ),
    imageAltText: enonicData.imageInfo.imageAltText,
    inputLists: {
      calculationInputs: [
        {
          type: 'inputText',
          inputListInRow: [
            {
              id: 'registration-number',
              labelText: enonicData.regNumberLabel,
              name: 'input-registrationNumber',
              onChange: handleOnChangeRegNo,
              onBlur: handleOnBlurRegNo,
              value: registrationNumber,
              className: '',
              labelClassName: 'input-label-text',
              inputWrapperClassName: 'reg-nr',
              invalid: !validRegNumber && !!regNumberErrorText,
              errorText: regNumberErrorText,
              prefixImage: (
                <img
                  src="https://cdn.gjensidige.no/claims-sweden/sweden-core/images/reg-nr-image.svg"
                  alt="Registreringsnummer"
                />
              )
            }
          ]
        },
        {
          type: 'inputText',
          inputListInRow: [
            {
              id: 'cpr-number',
              labelText: enonicData.cprNumberLabel,
              name: 'input-cprNumber',
              onChange: handleOnChangePersonNumber,
              onBlur: handleOnBlurPersonNumber,
              value: personNumber,
              className: 'mt-1',
              labelClassName: 'input-label-text',
              inputWrapperClassName: 'ssn',
              invalid: !validPersonNumber && !!personNumberErrorText,
              errorText: personNumberErrorText,
              helpText: '12 siffror ÅÅÅÅMMDDNNNN.',
              isHelpTextAlwaysVisible: true,
              moreInfoModal: {
                linkText: 'Varför frågar vi om personnummer?',
                title: 'Personnummer',
                content: getCprMoreInfoContent(),
                closeLabel: 'Stäng hjälptext',
                ariaLabel: 'Hjälp om personnummer',
                wrapperClassName: 'cpr-modal-wrapper',
                ariaDescribedBy: 'cpr-more-info'
              },
              inputMode: 'numeric'
            }
          ]
        }
      ],
      calculateBtn: {
        btnText: enonicData.calculateBtnLabel,
        onClick: gotoNextStep,
        enableTracking: true,
        disabled: !enableButton,
        className: 'calculate-price mt-2'
      }
    },
    additionalInfoLinkText: enonicData.personalDataHandlingInfoLinkText,
    additionalInfoLink: enonicData.personalDataHandlinkUrl,
    footerMessage: enonicData.footerMessage
  };

  return (
    <>
      <CalculationWidget {...motorcycleParams} />
      {isValidationModalOpen && (
        <CustomErrorModal
          isOpen={isValidationModalOpen}
          onClose={() => setIsValidationModalOpen(false)}
        />
      )}
    </>
  );
};
